import {Component, MeshComponent, Object3D, PhysXComponent} from '@wonderlandengine/api';
import {vec3} from 'gl-matrix';
import {hapticFeedback} from '../utils/haptic-feedback.js';
import {property} from '@wonderlandengine/api/decorators.js';
import {Cursor, CursorTarget} from '@wonderlandengine/components';
import {PhongMaterial} from '../types/materials.js';
import {CursorReticle, CursorType} from './cursor-reticle.js';
import {TextFade} from './text-fade.js';
import {SoundManagerInstance, Sounds} from '../managers/soundmanager.js';

/**
 * elevator-gate
 */
export class ElevatorGate extends Component {
    static TypeName = 'elevator-gate';

    @property.object()
    player?: Object3D;

    @property.object()
    doorInteractionObj?: Object3D;

    @property.object()
    textObj02?: Object3D;

    private lerpTime = 0;
    private startLoc: vec3 = new Float32Array(3);
    private endLoc: vec3 = new Float32Array(3);
    private lerpRatio = 0;
    private lerpResult: vec3 = new Float32Array(3);

    start() {
        /* Add Cursor Functions */
        const target = this.object.getComponent(CursorTarget);
        if (!target) {
            throw new Error('ElevatorGate: CursorTarget component is required');
        }
        target.onHover.add(this.onHover.bind(this));
        target.onUnhover.add(this.onUnHover.bind(this));
        target.onDown.add(this.onDown.bind(this));

        /* Lerp Open Gate */
        this.lerpTime = 100;
        this.startLoc = [104.74, -50.323, 116.556];
        this.endLoc = [104.74, -140.529, 116.556];
    }

    update(dt: number) {
        if (!this.object.parent) {
            console.warn('ElevatorGate parent not set ');
            return;
        }
        /* Lerp Open Gate */
        this.lerpTime += dt;
        this.lerpRatio = this.lerpTime / 1.3; /* 1.5 seconds */
        if (this.lerpTime < 1.3) {
            this.lerpResult = vec3.lerp(
                vec3.create(),
                this.startLoc,
                this.endLoc,
                this.lerpRatio
            );
            this.object.parent.setPositionLocal(this.lerpResult);
        }
    }

    onHover(_: Object3D, cursor: Cursor) {
        if (!this.object.parent) {
            console.warn('ElevatorGate parent not set');
            return;
        }

        hapticFeedback(cursor.object, 0.5, 50);
        /* Highlight */
        this.object.parent.getComponents(MeshComponent).forEach((a) => {
            const material = a.material as PhongMaterial;
            if (material) {
                material.emissiveColor = [0.05, 0.05, 0.05, 1];
            }
        });

        this.object.parent.children.forEach((c) => {
            const meshComponent = c.getComponent(MeshComponent);
            if (meshComponent) {
                const material = meshComponent.material as PhongMaterial;
                if (material) {
                    material.emissiveColor = [0.05, 0.05, 0.05, 1];
                }
            }
        });

        /* Cursor */
        if (this.player) {
            const cursorReticle = this.player.getComponent(CursorReticle);
            if (cursorReticle) {
                cursorReticle.changeCursorTexture(cursor, CursorType.Pointer);
            }
        } else {
            console.warn('ElevatorGate player property not set');
        }
    }

    onUnHover(_: Object3D, cursor: Cursor) {
        if (!this.object.parent) {
            console.warn('ElevatorGate parent not set');
            return;
        }

        hapticFeedback(cursor.object, 0.3, 50);
        /* Highlight Reset to Default */
        this.object.parent.getComponents(MeshComponent).forEach((a) => {
            const material = a.material as PhongMaterial;
            if (material) {
                material.emissiveColor = [0.0, 0.0, 0.0, 1];
            }
        });

        this.object.parent.children.forEach((c) => {
            const meshComponent = c.getComponent(MeshComponent);
            if (meshComponent) {
                const material = meshComponent.material as PhongMaterial;
                if (material) {
                    material.emissiveColor = [0.0, 0.0, 0.0, 1];
                }
            }
        });

        /* Cursor */
        if (this.player) {
            const cursorReticle = this.player.getComponent(CursorReticle);
            if (cursorReticle) {
                cursorReticle.changeCursorTexture(cursor, CursorType.Default);
            }
        } else {
            console.warn('ElevatorGate player property not set');
        }
    }

    onDown(_: Object3D, cursor: Cursor) {
        hapticFeedback(cursor.object, 1.0, 20);
        this.lerpTime = 0; /* Start Lerp */
        SoundManagerInstance.playSound(Sounds.elevatorGateOpen);

        const objectPhysx = this.object.getComponent(PhysXComponent);
        if (objectPhysx) {
            objectPhysx.active = false; /* Disable collision after opening door */
        }

        if (this.doorInteractionObj) {
            const doorPhysx = this.doorInteractionObj.getComponent(PhysXComponent);
            if (doorPhysx) {
                doorPhysx.active = true; /* Enable door collision */
            }
        } else {
            console.warn('ElevatorGate doorInteractionObj property not set');
        }

        const textObj02 = this.textObj02;
        if (textObj02) {
            const textFade = textObj02.getComponent(TextFade);
            if (textFade) {
                textFade.fadeOut();
            }
        } else {
            console.warn('ElevatorGate textObj02 property not set');
        }
    }
}
